import { useEffect, useState } from 'react';
import { FiX, FiCheckCircle, FiXCircle, FiAlertCircle, FiTrendingUp } from 'react-icons/fi';
import { getMarketInsight } from '../utils/api';

/**
 * AI 洞察模态框组件
 *
 * 在全屏模态框中显示市场的 AI 深度分析
 */
export function AIInsightModal({ open, onClose, marketSlug, marketTitle }) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !marketSlug) {
      return;
    }

    let mounted = true;

    async function fetchInsight() {
      try {
        setLoading(true);
        setError(null);
        const response = await getMarketInsight(marketSlug);

        if (mounted && response.code === 0) {
          setInsight(response.data);
        } else {
          setError(response.msg || '获取洞察失败');
        }
      } catch (err) {
        if (mounted) {
          setError(err.message || '网络错误');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchInsight();

    return () => {
      mounted = false;
    };
  }, [open, marketSlug]);

  if (!open) return null;

  const getSideStyle = (side) => {
    switch (side) {
      case 'Yes':
        return {
          icon: FiCheckCircle,
          bgColor: 'bg-emerald-500/10',
          borderColor: 'border-emerald-500/30',
          textColor: 'text-emerald-400',
          barColor: 'bg-emerald-500'
        };
      case 'No':
        return {
          icon: FiXCircle,
          bgColor: 'bg-rose-500/10',
          borderColor: 'border-rose-500/30',
          textColor: 'text-rose-400',
          barColor: 'bg-rose-500'
        };
      default:
        return {
          icon: FiAlertCircle,
          bgColor: 'bg-amber-500/10',
          borderColor: 'border-amber-500/30',
          textColor: 'text-amber-400',
          barColor: 'bg-amber-500'
        };
    }
  };

  const dimensionIcons = {
    market_structure: '📊',
    price_behavior: '📈',
    participants: '👥',
    fundamentals: '🌐',
    mechanism: '⚙️',
    cross_market: '🔄'
  };

  const dimensionLabels = {
    market_structure: '市场结构',
    price_behavior: '价格行为',
    participants: '参与者分析',
    fundamentals: '基本面分析',
    mechanism: '机制分析',
    cross_market: '跨市场对比'
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl bg-slate-900/95 border border-slate-700/50 backdrop-blur-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-slate-700/50 bg-slate-900/95 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <FiTrendingUp className="w-6 h-6 text-sky-400" />
            <div>
              <h2 className="text-xl font-bold text-white">AI 深度洞察</h2>
              <p className="text-sm text-slate-400 mt-1">{marketTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
            aria-label="关闭"
          >
            <FiX className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading && (
            <div className="space-y-4">
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 border-4 border-sky-400/30 border-t-sky-400 rounded-full animate-spin" />
                  <p className="text-sm text-slate-400">AI 正在分析市场...</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="p-6 rounded-xl bg-red-500/10 border border-red-500/20">
              <div className="flex items-center gap-2 text-red-400">
                <FiAlertCircle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {!loading && !error && insight && (
            <div className="space-y-6">
              {/* 推荐卡片 */}
              {insight.recommendation && (() => {
                const sideStyle = getSideStyle(insight.recommendation.side);
                const SideIcon = sideStyle.icon;
                const confidencePercent = Math.round(insight.recommendation.confidence * 100);
                const expectedEdgePercent = Math.round(insight.recommendation.expected_edge * 100);

                return (
                  <div className={`p-6 rounded-xl backdrop-blur-sm border ${sideStyle.bgColor} ${sideStyle.borderColor}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <SideIcon className={`w-6 h-6 ${sideStyle.textColor}`} />
                        <div>
                          <span className={`font-bold text-2xl ${sideStyle.textColor}`}>
                            推荐 {insight.recommendation.side}
                          </span>
                          <p className="text-xs text-white/60 mt-1">AI 置信度 {confidencePercent}%</p>
                        </div>
                      </div>
                    </div>

                    <p className="text-base text-white/90 mb-4 leading-relaxed">
                      {insight.recommendation.rationale}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* 置信度条 */}
                      <div>
                        <div className="flex justify-between text-xs text-white/60 mb-2">
                          <span>置信度</span>
                          <span className="font-semibold">{confidencePercent}%</span>
                        </div>
                        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${sideStyle.barColor} transition-all duration-500`}
                            style={{ width: `${confidencePercent}%` }}
                          />
                        </div>
                      </div>

                      {/* 预期优势 */}
                      <div>
                        <div className="flex justify-between text-xs text-white/60 mb-2">
                          <span>预期优势</span>
                          <span className="font-semibold">{expectedEdgePercent}%</span>
                        </div>
                        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-400 transition-all duration-500"
                            style={{ width: `${expectedEdgePercent}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* 六维度分析 */}
              {insight.analysis && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <span className="text-sky-400">●</span>
                    深度分析
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(insight.analysis).map(([dimension, points]) => {
                      if (!points || points.length === 0) return null;

                      return (
                        <div
                          key={dimension}
                          className="p-4 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-colors"
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-2xl">{dimensionIcons[dimension]}</span>
                            <span className="text-sm font-semibold text-white">
                              {dimensionLabels[dimension]}
                            </span>
                          </div>

                          <ul className="space-y-2">
                            {points.map((point, idx) => (
                              <li key={idx} className="text-sm text-white/80 leading-relaxed pl-4 relative">
                                <span className="absolute left-0 top-2 w-1.5 h-1.5 bg-sky-400 rounded-full" />
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="pt-4 border-t border-slate-700/50">
                <p className="text-xs text-slate-500 text-center">
                  本分析由 AI 生成，仅供参考。投资决策需自行判断风险。
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
